// English translations. Keep key parity with fr.ts.

import type { FrResources } from "./fr";

export const en: FrResources = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    loading: "Loading…",
    error: "Error",
    success: "Success",
    serverError: "Server error",
    notFound: "Not found",
    forbidden: "Access denied",
    unauthorized: "Not authenticated",
    none: "None",
    all: "All",
    search: "Search",
    filter: "Filter",
    actions: "Actions",
    name: "Name",
    status: "Status",
    createdAt: "Created",
    updatedAt: "Updated",
    never: "never",
    refresh: "Refresh",
    add: "Add",
    new: "New",
    back: "Back",
    retry: "Retry",
    skip: "Skip",
    abort: "Abort",
    stop: "Stop",
    pause: "Pause",
    resume: "Resume",
    start: "Start",
    apply: "Apply",
    copy: "Copy",
    copied: "Copied",
    optional: "optional",
    required: "required",
  },
  sidebar: {
    dashboard: "Dashboard",
    phones: "Phones",
    scripts: "Scripts",
    jobs: "Jobs",
    accounts: "IG Accounts",
    failures: "Failures",
    shareCenter: "Share Center",
    users: "Users",
    documentation: "Documentation",
    logout: "Sign out",
  },
  auth: {
    login: {
      title: "Sign in",
      subtitle: "Access the Velvet panel",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      submitting: "Signing in…",
      error: "Invalid credentials",
    },
    logout: "Sign out",
    sessionExpired: "Your session has expired. Please sign in again.",
  },
  status: {
    online: "Online",
    offline: "Offline",
    busy: "Busy",
    error: "Error",
    active: "Active",
    suspended: "Suspended",
    deleted: "Deleted",
    pending_validation: "Pending review",
    pending: "Pending",
    dispatched: "Dispatched",
    running: "Running",
    paused: "Paused",
    completed: "Completed",
    cancelled: "Cancelled",
    success: "Success",
    failed: "Failed",
    debug: "debug",
    info: "info",
    warn: "warn",
  },
  dashboard: {
    title: "Dashboard",
    subtitle: "Overview of the Velvet farm.",
    realtimeOn: "Realtime connected",
    realtimeOff: "Realtime disconnected",
    sectionPhones: "Phones",
    sectionAccounts: "Instagram accounts",
    seeAllPhones: "View all phones →",
    seeAllAccounts: "View all accounts →",
    cards: {
      total: "Total",
      online: "Online",
      busy: "Busy",
      offline: "Offline",
      error: "Error",
      active: "Active",
      suspended: "Suspended",
      deleted: "Deleted",
      pendingReview: "Pending review",
    },
  },
  phones: {
    title: "Phones",
    enrolledCount: "{{count}} phone enrolled",
    enrolledCount_plural: "{{count}} phones enrolled",
    addBtn: "Add a phone",
    columns: {
      name: "Name",
      status: "Status",
      model: "Model",
      url: "Agent URL",
      lastHeartbeat: "Last heartbeat",
    },
    actions: {
      stream: "View stream",
      envVars: "Environment variables",
      reboot: "Reboot",
      delete: "Delete",
      rename: "Rename",
    },
    confirmDelete: "Delete this phone? Linked jobs/runs/accounts will also be deleted.",
    empty: "No phones enrolled. Follow the install guide to add one.",
    envModal: {
      title: "Environment variables — {{name}}",
      description: "Variables exposed to scripts via ${VAR}. Editable without redeploying.",
      keyPlaceholder: "VAR_NAME",
      valuePlaceholder: "value",
      addRow: "Add a variable",
      empty: "No variables defined.",
    },
    addModal: {
      title: "Add a phone",
      name: "Name",
      connectionUrl: "Connection URL (port 8889)",
      spoofedModel: "Spoofed model (optional)",
      submit: "Create",
      enrollSshTitle: "SSH enrollment command",
      enrollSshHint: "Copy and run this one-liner on the phone (shown only once).",
    },
  },
  phoneStream: {
    title: "Stream {{name}}",
    connecting: "Connecting…",
    reconnecting: "Reconnecting (attempt {{n}}) in {{s}}s",
    fullscreen: "Fullscreen",
    reboot: "Reboot",
    ocr: "OCR",
    controls: {
      home: "Home",
      appSwitcher: "App switcher",
      lock: "Lock",
      volumeUp: "Volume +",
      volumeDown: "Volume −",
    },
    notReachable: "Phone unreachable on {{addr}} (timeout). Is the H.264 stream server running?",
    instructions: "Click-drag on the phone to swipe, single click to tap, long click for long-press.",
  },
  scripts: {
    title: "Scripts",
    addBtn: "New script",
    importBtn: "Import JSON",
    columns: {
      name: "Name",
      type: "Type",
      lastUpdate: "Last update",
      actions: "Actions",
    },
    actions: {
      edit: "Edit",
      duplicate: "Duplicate",
      export: "Export",
      delete: "Delete",
      launch: "Launch a job",
    },
    confirmDelete: "Delete script {{name}}?",
    empty: "No scripts. Create one with the editor or import a JSON.",
    editor: {
      title: "Script editor",
      saveBtn: "Save",
      saving: "Saving…",
      validateBtn: "Validate structure",
      runBtn: "Launch a job",
      formatBtn: "Format",
      nameLabel: "Name",
      descriptionLabel: "Description",
      jobTypeLabel: "Job type",
      contentLabel: "Content (JSONC)",
      isActiveLabel: "Active",
      validationOk: "Valid structure",
      validationError: "Validation error",
      unsaved: "Unsaved changes",
      saved: "Saved",
    },
  },
  jobs: {
    title: "Jobs",
    countSuffix: "Auto-refresh every 5 s.",
    countOne: "{{count}} job",
    countOther: "{{count}} jobs",
    newBtn: "New job",
    columns: {
      name: "Name",
      script: "Script",
      phone: "Phone",
      mode: "Mode",
      progress: "Progress",
      status: "Status",
      created: "Created",
    },
    empty: "No jobs match this filter.",
    detail: {
      tabs: {
        runs: "Runs",
        logs: "Logs",
        script: "Script",
      },
      stats: {
        progress: "Progress",
        success: "Success",
        failed: "Failed",
        cancelled: "Cancelled",
        pending: "Pending",
      },
      pausedBanner: {
        title: "Run paused on failure",
        manualTitle: "Run manually paused",
        manualHint: "The script is frozen on the device. Take control via the stream, then Continue to resume where it left off.",
        stepLabel: "Step",
        runLabel: "Run",
        pausedSince: "paused {{ago}}",
        autoAbort: "Auto-abort after 30 min if no action.",
        retry: "Retry step",
        skip: "Skip",
        continue: "Continue",
        openStream: "Open stream",
        abort: "Abort",
        confirmAbort: "Abort this run? It will be marked as failed.",
        screenshotHint: "Snapshot captured at fail time. Use the stream to fix manually before Retry.",
      },
      runs: {
        empty: "No runs for this job.",
        columns: {
          id: "ID",
          status: "Status",
          started: "Started",
          duration: "Duration",
          result: "Result / Error",
        },
      },
      logs: {
        empty: "No logs for this job's runs (the agent may not have sent anything yet).",
      },
      pause: "Pause",
      resume: "Resume",
      stop: "Stop",
      confirmStop: "Stop this job? The current run will be cancelled on the phone.",
      live: "Live",
      offline: "Offline",
    },
    picker: {
      title: "Which script to launch?",
      noPhones: "No phone available. Ask an admin to assign a phone to you.",
      noScripts: "No active script.",
    },
    launch: {
      title: "Launch a job — {{script}}",
      jobName: "Job name",
      phone: "Phone",
      mode: "Mode",
      modeOneShot: "One-shot (1 run)",
      modeLoop: "Loop (multiple runs)",
      maxRunsLabel: "Limit",
      maxRunsLimited: "Limited",
      maxRunsInfinite: "Infinite",
      maxRunsCount: "Number of runs",
      submit: "Launch",
      submitting: "Launching…",
      noPhones: "No phones enrolled.",
    },
  },
  accounts: {
    title: "Instagram accounts",
    countSuffix: "{{n}} account total",
    countSuffix_plural: "{{n}} accounts total",
    columns: {
      username: "Username",
      email: "Email",
      phone: "IG Phone",
      twoFa: "2FA",
      status: "Status",
      createdAt: "Created",
      device: "Created on",
      fullName: "Name",
      followers: "Followers",
      posts: "Posts",
      lastSync: "Last sync",
      source: "Source",
    },
    actions: {
      copy: "Copy",
      exportTxt: "Export (txt)",
      delete: "Delete",
      add: "Add account",
      view: "View details",
      syncNow: "Fetch from Instagram",
      syncFailed: "Fetch failed",
      retrySync: "Retry sync",
      edit: "Edit",
      save: "Save",
      cancel: "Cancel",
      copyCreds: "Copy credentials",
      openProfile: "Open profile",
    },
    confirmDelete: "Delete this account? Credentials will be lost.",
    empty: "No accounts created for this filter.",
    filters: {
      status: "Status",
      device: "Phone",
      search: "Search (username, name, email)",
      source: "Source",
      syncStatus: "Sync",
    },
    source: {
      agent: "Auto",
      manual: "Manual",
    },
    sync: {
      never: "Never",
      ok: "OK",
      stale: "Stale",
      error: "Error",
      checkpoint: "Checkpoint",
      banned: "Banned",
    },
    stats: {
      total: "Total",
      active: "Active",
      suspended: "Suspended",
      deleted: "Deleted",
      pending: "Pending review",
      postsToday: "Posts today",
      created: "Accounts created",
      verification: "Verification needed",
    },
    pagination: {
      prev: "Previous",
      next: "Next",
      page: "Page {{page}} / {{pages}}",
    },
    add: {
      title: "Add account",
      username: "Username (required)",
      igUserId: "IG user ID",
      fullName: "Full name",
      email: "Email",
      phoneNumber: "Phone number",
      password: "Password",
      twoFaSecret: "2FA secret",
      proxy: "Proxy",
      notes: "Notes",
      submit: "Create",
      submitting: "Creating…",
      error: "Creation failed",
    },
    detail: {
      back: "Back",
      followers: "Followers",
      following: "Following",
      posts: "Posts",
      overview: "Growth",
      noMetrics: "No metrics yet.",
      postsTitle: "Posts",
      noPosts: "No synced posts.",
      credentials: "Credentials",
      password: "Password",
      twoFa: "2FA secret",
      notes: "Notes",
      notesPlaceholder: "Internal notes…",
      info: "Info",
      igUserId: "IG user ID",
      device: "Created on",
      proxy: "Proxy",
      runId: "Origin run",
      createdAt: "Created at",
      lastSync: "Last sync",
      reveal: "Show",
      hide: "Hide",
      likes: "likes",
      comments: "comments",
      views: "views",
      syncQueued: "Sync requested",
      syncInProgress: "Fetching Instagram profile… (up to ~45s)",
      syncErrorPrefix: "Fetch failed",
      notFound: "Account not found",
      bioPlaceholder: "No bio",
      creationIphone: "Creation iPhone",
      statPosts: "Posts made",
      statViews: "Total views",
      statLikes: "Total likes",
      statWarmup: "Warm-up hours",
      notesTitle: "Notes",
      lastPostTitle: "Last post",
      timeline: {
        title: "Timeline",
        empty: "No posts or warm-up sessions yet.",
        warmupLabel: "{{min}} min of warm-up",
        postLabel: {
          photo: "Photo posted",
          video: "Video posted",
          carousel: "Carousel posted",
          reel: "Reel posted",
        },
      },
      warmup: {
        addTitle: "Add a warm-up session",
        start: "Start",
        duration: "Duration (min)",
        note: "Note (optional)",
        add: "Add",
        delete: "Delete session",
        errStart: "Invalid start date",
        errDuration: "Invalid duration (minutes > 0)",
        errAdd: "Add failed",
        errDelete: "Delete failed",
      },
    },
    range: {
      d7: "7 d",
      d30: "30 d",
      d90: "90 d",
      all: "All",
    },
  },
  failures: {
    title: "Failures",
    subtitle: "Script error captures (Phase 0 auto-rescue)",
    columns: {
      created: "Date",
      script: "Script",
      phone: "Phone",
      step: "Step",
      error: "Error",
      screenshot: "Snapshot",
    },
    empty: "No failures captured.",
    askAi: "Ask AI",
    asking: "Analyzing…",
    apply: "Apply",
    ignore: "Ignore",
    detail: {
      contextTab: "Context",
      ocrTab: "OCR",
      varsTab: "Runtime variables",
      rescueTab: "AI attempts",
      stepDef: "Step definition",
      precedingSteps: "Preceding steps",
      errorMessage: "Error message",
      noScreenshot: "No screenshot available.",
    },
  },
  users: {
    title: "Users",
    countSuffix: "{{n}} account",
    countSuffix_plural: "{{n}} accounts",
    addBtn: "Add a user",
    columns: {
      user: "User",
      role: "Role",
      phones: "Phones",
      status: "Status",
      lastLogin: "Last login",
    },
    role: {
      admin: "Admin",
      employee: "Employee",
    },
    fullAccess: "full access",
    none: "none",
    create: {
      title: "Add a user",
      email: "Email",
      name: "Name",
      password: "Initial password (8 chars min)",
      passwordHint: "The user can change it later",
      role: "Role",
      roleEmployee: "Employee (limited to assigned phones)",
      roleAdmin: "Admin (full access)",
      phones: "Assigned phones",
      noPhones: "No phones enrolled",
      submit: "Create",
      submitting: "Creating…",
    },
    assign: {
      title: "Phones for {{name}}",
      hint: "Tick the phones {{email}} has access to. Changes apply immediately (~60s cache TTL).",
      saveCount: "Save ({{n}})",
    },
    resetPassword: {
      title: "Reset password — {{email}}",
      label: "New password (8 chars min)",
      submit: "Reset",
      submitting: "Resetting…",
      done: "✓ Password reset. Share it with {{name}}:",
      ok: "OK",
      tooShort: "Minimum 8 characters",
    },
    confirmDelete: "Delete user {{email}}? This cannot be undone.",
    selfRoleLocked: "You can't change your own role",
    selfDeactivateLocked: "You can't deactivate your own account",
    selfDeleteLocked: "You can't delete your own account",
  },
  shareCenter: {
    title: "Share Center",
    subtitle: "Media and caption library shared with the phones.",
    tabs: {
      media: "Media",
      text: "Text",
    },
    media: {
      uploadBtn: "Upload media",
      uploading: "Uploading…",
      count: "{{n}} media",
      count_plural: "{{n}} media",
      empty: "No media yet. Upload an image or a video.",
      labelPlaceholder: "Label (optional)",
      tagsPlaceholder: "comma-separated tags (optional)",
      confirmDelete: "Delete this media? The original will be lost.",
      preview: "Preview",
      downloadOriginal: "Original",
      downloadCleaned: "Cleaned",
      cleaning: "Cleaning…",
      downloadError: "Download failed",
    },
    text: {
      addBtn: "New snippet",
      count: "{{n}} snippet",
      count_plural: "{{n}} snippets",
      empty: "No text snippet yet. Add a promo caption.",
      titleLabel: "Title",
      bodyLabel: "Text",
      tagsLabel: "Tags (comma-separated)",
      titlePlaceholder: "e.g. Spring promo caption",
      bodyPlaceholder: "The text to copy-paste…",
      confirmDelete: "Delete this snippet?",
      copy: "Copy",
      copied: "Copied",
      save: "Save",
    },
  },
  documentation: {
    title: "Documentation",
    subtitle: "Useful commands and troubleshooting",
    sections: {
      daemon: "Daemon (velvetfingerd)",
      install: "Tweak install / update",
      troubleshooting: "Troubleshooting",
    },
    copyCmd: "Copy command",
    copied: "Copied",
  },
  language: {
    label: "Language",
    fr: "Français",
    en: "English",
    detected: "Auto-detected language",
    saved: "Choice saved",
  },
};
